/**
 * A small query language over the league's own record, and the thing that runs it.
 *
 * WHY THIS EXISTS AND WHY IT IS SHAPED LIKE THIS.
 *
 * "Most 0.0 scores on a winner's starting lineup" is a real question a manager will ask and no
 * screen in this app answers. The obvious way to build that — hand a language model a summary of
 * the archive and print what it says — produces an answer that reads beautifully and cannot be
 * checked, in a league where every number gets quoted back at somebody in an argument. A wrong
 * record is worse than no record.
 *
 * So the model never sees the data and never produces a number. It does one job: turn a sentence
 * into a QUERY. The app runs the query against the archive itself, so every figure on the screen
 * was computed here, from the same rows the rest of the app uses, and can be shown alongside the
 * query that produced it. If the model misunderstands the question the query will be visibly wrong
 * — which is a failure you can see, argue with, and correct.
 *
 * Nothing in this file knows that a model exists. It is a query language and an interpreter; it is
 * tested as one, and the "ask" screen is a thin thing on top.
 */

import { allBoxGames, linePoints, linePosition, blanksAreZero, splitBoxName } from './boxScores.js';
import { gmFor, managerShort } from './identity.js';

/* -- the rows you can ask about --------------------------------------------------------------- */

/**
 * Two shapes, chosen because they are the two units the league actually talks in: "a team in a
 * week" and "a player in a week". Almost every records question is one or the other.
 */
export type Source = 'sides' | 'lines';

/** One team's half of one game. */
export interface SideRow {
  season: number;
  week: number;
  /** `regular`, `playoff` or `consolation`, as the archive records it. */
  kind: string;
  gm: string | null;
  team: string;
  points: number | null;
  opponentGm: string | null;
  opponentTeam: string;
  opponentPoints: number | null;
  won: boolean;
  tied: boolean;
  margin: number | null;
  /** What the starters scored, and what was left sitting on the bench. */
  started: number;
  bench: number;
  /** How many starters, and how many of those scored nothing. */
  starters: number;
  starterZeroes: number;
  /** A stable id so `groupBy: 'game'` can name the matchup it grouped. */
  game: string;
}

/** One player in one lineup in one week. */
export interface LineRow {
  season: number;
  week: number;
  kind: string;
  gm: string | null;
  team: string;
  player: string;
  /** The lineup slot: `QB`, `W/R/T`, `BN`. */
  slot: string;
  /** The position, resolved across the archive — see `linePosition`. */
  position: string | null;
  points: number | null;
  /** False for a bench line. */
  startedHim: boolean;
  /** Did the TEAM win that week. */
  won: boolean;
  teamPoints: number | null;
  opponentGm: string | null;
  game: string;
}

export type Row = SideRow | LineRow;

/* -- the query ------------------------------------------------------------------------------- */

export type Op = '=' | '!=' | '>' | '>=' | '<' | '<=' | 'in' | 'contains';

export interface Filter {
  field: string;
  op: Op;
  value: string | number | boolean | null | (string | number)[];
}

/**
 * `pct` is the odd one out, and it is the reason half the interesting questions became askable.
 *
 * "Who's the worst manager?" is a RATE question, and a language of sums and counts cannot ask it:
 * count somebody's losses and you have ranked the men who have been in the league longest. So
 * `pct` reads a true/false field across a group and gives back the share that were true — win
 * percentage, how often a manager beats one particular opponent, the share of weeks a position
 * was left on the bench.
 */
export type Fn = 'count' | 'sum' | 'avg' | 'max' | 'min' | 'pct';

export interface Query {
  from: Source;
  where?: Filter[];
  /** A field to group on. Omit to measure over every matching row at once. */
  groupBy?: string;
  measure: { fn: Fn; field?: string };
  order?: 'desc' | 'asc';
  limit?: number;
  /**
   * Drop groups thinner than this before ranking.
   *
   * A rate over four games is not a record, it is an accident — and without a floor the answer to
   * "worst win percentage" is always whoever played fewest games and lost them. `runQuery` reports
   * how many groups the floor removed, so it can never quietly bury somebody.
   */
  minRows?: number;
  /**
   * Rank by the measure, or read in group order.
   *
   * A TREND IS NOT A LEAGUE TABLE. "How has QB scoring moved over ten years" grouped by season and
   * sorted by value hands back the seasons shuffled into order of size, which is the one ordering
   * that makes a trend impossible to see. `sort: 'key'` puts them back in season order, and it is
   * what any question with the word "over" or "by year" in it actually wants.
   */
  sort?: 'value' | 'key';
  /**
   * The model's one-line restatement of the question, in words, carrying no figures.
   *
   * Prose from a model is the thing this whole design exists to avoid, so it is allowed exactly
   * this much: a sentence saying what it understood you to be asking. It cannot state a record,
   * because `readingOf` refuses to let it contain a digit.
   */
  reading?: string;
}

/** Enough to find the matchup on the Games screen and open its box score. */
export interface GameLink { season: number; week: number; team: string }

export interface ResultRow {
  /** The group's value, or `'all'` when the query did not group. */
  key: string;
  value: number;
  /** How many rows fell into this group, which is not the same as the measure. */
  rows: number;
  /** A human line naming the group — the matchup, the manager, the season. */
  label: string;
  /**
   * The game this row is about, when it is about exactly one.
   *
   * An answer you cannot get behind is half an answer: "21 teams scored exactly 100" invites
   * "which ones", and the box score for every one of them is already in this app. A row that
   * names a single matchup carries the way to it.
   */
  ref?: GameLink;
}

export interface QueryResult {
  rows: ResultRow[];
  /** How many source rows the filters kept, before grouping. */
  matched: number;
  /** How many the source had in total. Together these say how selective the question was. */
  scanned: number;
  /**
   * THE ROWS THEMSELVES, up to a cap.
   *
   * A count on its own is the least useful true thing an archive can tell you. "21" is an answer
   * nobody can check, argue with, or enjoy; twenty-one dated matchups with the box score one tap
   * behind each is the thing somebody screenshots into the group chat.
   */
  examples: ResultRow[];
  /**
   * WHAT THE NUMBER IS STANDING NEXT TO.
   *
   * A record with nothing around it is a number, and a number is the part of an answer people
   * argue about least usefully. "Amit, 41%" invites "out of how many, and against what?" — and
   * every fact needed to head that off is already sitting in the rows that were just counted: how
   * many seasons it spans, where the rest of the field sits, how many groups were too thin to
   * rank, how much of the archive the question actually reached.
   *
   * Computed HERE, from the same rows, exactly like the measure. No model wrote a word of it.
   */
  context: string[];
  /** How many groups the query made, before `minRows` and `limit` cut it down. */
  groups: number;
}

/** How many matching rows to hand back as evidence. Enough to scan, not enough to scroll for ever. */
export const MAX_EXAMPLES = 25;

export const MAX_LIMIT = 50;

/* -- building the rows ------------------------------------------------------------------------ */

const gameId = (season: number, week: number, a: string, b: string) =>
  `${season}|${week}|${[a, b].sort().join(' v ')}`;

let cache: { v: number; sides: SideRow[]; lines: LineRow[] } | null = null;

/**
 * Both row sets, built once from the box-score archive.
 *
 * Deliberately built from the BOX SCORES rather than from the standings, because the questions
 * this is for are about lineups — who was started, who was benched, what each of them scored — and
 * the standings carry none of that. A game with no box score is not in here at all, which the
 * result reports as `scanned` rather than pretending to a coverage it does not have.
 */
export function rowsFor(source: Source): Row[] {
  const games = allBoxGames();
  if (!cache || cache.v !== games.version) {
    const sides: SideRow[] = [];
    const lines: LineRow[] = [];

    for (const g of games.games) {
      const id = gameId(g.s, g.w, g.a.team, g.b.team);
      for (const [me, them] of [[g.a, g.b], [g.b, g.a]] as const) {
        const zeroed = blanksAreZero(me);
        const pts = (l: (typeof me.st)[number]) => linePoints(l, zeroed);

        const started = me.st.reduce((n, l) => n + (pts(l) ?? 0), 0);
        const bench = me.bn.reduce((n, l) => n + (pts(l) ?? 0), 0);
        const mine = me.pts;
        const theirs = them.pts;

        const gm = gmFor(g.s, { gm: null, team: me.team });
        const oppGm = gmFor(g.s, { gm: null, team: them.team });
        const won = mine !== null && theirs !== null && mine > theirs;
        const tied = mine !== null && theirs !== null && mine === theirs;

        sides.push({
          season: g.s, week: g.w, kind: 'regular',
          gm, team: me.team,
          points: mine,
          opponentGm: oppGm, opponentTeam: them.team, opponentPoints: theirs,
          won, tied,
          margin: mine !== null && theirs !== null ? Math.round((mine - theirs) * 100) / 100 : null,
          started: Math.round(started * 100) / 100,
          bench: Math.round(bench * 100) / 100,
          starters: me.st.length,
          starterZeroes: me.st.filter((l) => pts(l) === 0).length,
          game: id,
        });

        for (const [group, startedHim] of [[me.st, true], [me.bn, false]] as const) {
          for (const l of group) {
            const { name } = splitBoxName(l[0]);
            lines.push({
              season: g.s, week: g.w, kind: 'regular',
              gm, team: me.team,
              player: name,
              slot: l[1],
              position: linePosition(l),
              points: pts(l),
              startedHim,
              won,
              teamPoints: mine,
              opponentGm: oppGm,
              game: id,
            });
          }
        }
      }
    }
    cache = { v: games.version, sides, lines };
  }
  return source === 'sides' ? cache.sides : cache.lines;
}

/* -- running it ------------------------------------------------------------------------------- */

/** Every field a query may name, per source. Also what the model is told it may use. */
export const FIELDS: Record<Source, string[]> = {
  sides: [
    'season', 'week', 'kind', 'gm', 'team', 'points', 'opponentGm', 'opponentTeam',
    'opponentPoints', 'won', 'tied', 'margin', 'started', 'bench', 'starters', 'starterZeroes',
    'game',
  ],
  lines: [
    'season', 'week', 'kind', 'gm', 'team', 'player', 'slot', 'position', 'points', 'startedHim',
    'won', 'teamPoints', 'opponentGm', 'game',
  ],
};

export class QueryError extends Error {}

/**
 * Refuse a query that names something that does not exist, rather than quietly returning nothing.
 *
 * A model that invents a field is the most likely way this goes wrong, and an empty table is the
 * worst possible way to report it — indistinguishable from "no such record exists", which is
 * itself an interesting answer. So it is an error with the offending name in it.
 */
export function validate(q: Query): void {
  const fields = FIELDS[q.from];
  if (!fields) throw new QueryError(`There is nothing called '${q.from}' to ask about.`);
  const known = (f: string) => {
    if (!fields.includes(f)) {
      throw new QueryError(`'${f}' is not something a ${q.from} row has. Try: ${fields.join(', ')}.`);
    }
  };
  for (const w of q.where ?? []) known(w.field);
  if (q.groupBy) known(q.groupBy);
  if (q.measure.field) known(q.measure.field);
  if (q.measure.fn !== 'count' && !q.measure.field) {
    throw new QueryError(`'${q.measure.fn}' needs a field to work on.`);
  }
  // A share of something that was never true or false is not a share of anything.
  if (q.measure.fn === 'pct' && !BOOLEAN_FIELDS.includes(q.measure.field!)) {
    throw new QueryError(
      `A percentage needs a yes/no field. Those are: ${BOOLEAN_FIELDS.join(', ')}.`,
    );
  }
}

/** The true/false fields — the only ones a percentage means anything on. */
const BOOLEAN_FIELDS = ['won', 'tied', 'startedHim'];

function valueOf(row: Row, field: string): unknown {
  return (row as unknown as Record<string, unknown>)[field];
}

function matches(row: Row, f: Filter): boolean {
  const v = valueOf(row, f.field);
  switch (f.op) {
    case '=': return v === f.value;
    case '!=': return v !== f.value;
    case 'in': return Array.isArray(f.value) && (f.value as unknown[]).includes(v);
    case 'contains':
      return typeof v === 'string' && typeof f.value === 'string'
        && v.toLowerCase().includes(f.value.toLowerCase());
    default: {
      // The ordered comparisons are only meaningful on numbers, and a null is not a small number
      // — a side with no recorded total must not count as scoring less than everybody.
      if (typeof v !== 'number' || typeof f.value !== 'number') return false;
      if (f.op === '>') return v > f.value;
      if (f.op === '>=') return v >= f.value;
      if (f.op === '<') return v < f.value;
      return v <= f.value;
    }
  }
}

function measureOf(rows: Row[], m: Query['measure']): number {
  if (m.fn === 'count') return rows.length;

  if (m.fn === 'pct') {
    const bools = rows
      .map((r) => valueOf(r, m.field!))
      .filter((v): v is boolean => typeof v === 'boolean');
    if (!bools.length) return 0;
    return round((bools.filter(Boolean).length / bools.length) * 100);
  }

  const nums = rows
    .map((r) => valueOf(r, m.field!))
    // A boolean is a perfectly good 1 or 0 to sum or average, and asking for the average of `won`
    // is a thing anybody would try. Silently dropping them made that question answer zero.
    .map((v) => (typeof v === 'boolean' ? (v ? 1 : 0) : v))
    .filter((v): v is number => typeof v === 'number');
  if (!nums.length) return 0;
  if (m.fn === 'sum') return round(nums.reduce((a, b) => a + b, 0));
  if (m.fn === 'avg') return round(nums.reduce((a, b) => a + b, 0) / nums.length);
  if (m.fn === 'max') return round(Math.max(...nums));
  return round(Math.min(...nums));
}

const round = (n: number) => Math.round(n * 100) / 100;

/** The measures that are means. A mean over one row is not a mean. */
const RATE_FNS: Fn[] = ['avg', 'pct'];

/**
 * The floor a grouped average takes when the question did not set one.
 *
 * Five, not because five is principled but because it is the smallest number that stops a single
 * good week outranking a career, which is the failure this exists to prevent. It is reported
 * every time it removes anything, and any question may override it.
 */
export const DEFAULT_FLOOR = 5;

/** Numeric group keys sort as numbers; everything else as text. */
function cmpKey(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);
  return Number.isFinite(na) && Number.isFinite(nb) ? na - nb : a.localeCompare(b);
}

/** Where a row happened, when it happened in one place. */
function linkFor(r: Row | undefined): GameLink | undefined {
  if (!r) return undefined;
  return { season: r.season, week: r.week, team: r.team };
}

/** One matching row, named and linked — the evidence under an aggregate. */
function exampleOf(r: Row): ResultRow {
  const who = r.gm ? managerShort(r.gm) : r.team;
  const opp = r.opponentGm ? managerShort(r.opponentGm) : null;
  const isLine = 'player' in r;
  const value = isLine ? (r as LineRow).points ?? 0 : (r as SideRow).points ?? 0;
  return {
    key: `${r.game}|${isLine ? (r as LineRow).player : r.team}`,
    value,
    rows: 1,
    label: isLine
      ? `${r.season} wk ${r.week} · ${(r as LineRow).player} · ${who}`
      : `${r.season} wk ${r.week} · ${who}${opp ? ` v ${opp}` : ''}`,
    ref: linkFor(r),
  };
}

/** A group key a person can read. `game` is an id and gets spelled out. */
function labelFor(key: string, field: string | undefined, sample: Row | undefined): string {
  if (!field) return 'all rows';
  if (field === 'game' && sample) {
    const gm = sample.gm ? managerShort(sample.gm) : sample.team;
    const opp = sample.opponentGm ? managerShort(sample.opponentGm) : '—';
    return `${sample.season} wk ${sample.week} · ${gm} v ${opp}`;
  }
  if ((field === 'gm' || field === 'opponentGm') && key && key !== 'null') return managerShort(key);
  return key === 'null' ? '—' : key;
}

export function runQuery(q: Query): QueryResult {
  validate(q);
  const all = rowsFor(q.from);
  const kept = (q.where ?? []).reduce(
    (rows, f) => rows.filter((r) => matches(r, f)),
    all,
  );

  const limit = Math.min(Math.max(1, q.limit ?? 10), MAX_LIMIT);
  const desc = q.order !== 'asc';

  /*
   * The evidence, whether or not the query grouped. Ordered by the measure's field where there is
   * one, so "highest single week" hands back the highest weeks rather than the first it happened
   * to walk past.
   */
  const sorted = q.measure.field
    ? [...kept].sort((a, b) => {
      const av = valueOf(a, q.measure.field!);
      const bv = valueOf(b, q.measure.field!);
      const an = typeof av === 'number' ? av : -Infinity;
      const bn = typeof bv === 'number' ? bv : -Infinity;
      return desc ? bn - an : an - bn;
    })
    : kept;
  const examples = sorted.slice(0, MAX_EXAMPLES).map(exampleOf);

  if (!q.groupBy) {
    const rows = [{
      key: 'all', value: measureOf(kept, q.measure), rows: kept.length, label: 'all rows',
    }];
    return {
      rows,
      matched: kept.length,
      scanned: all.length,
      examples,
      groups: 1,
      context: contextFor({
        q, kept, all, ranked: rows, desc, floor: 0, thin: 0, unnamed: 0, total: 1,
      }),
    };
  }

  const groups = new Map<string, Row[]>();
  for (const r of kept) {
    const k = String(valueOf(r, q.groupBy));
    const g = groups.get(k) ?? [];
    g.push(r);
    groups.set(k, g);
  }

  const scored = [...groups.entries()].map(([key, rs]) => ({
    key,
    value: measureOf(rs, q.measure),
    rows: rs.length,
    label: labelFor(key, q.groupBy, rs[0]),
    // A group is linkable when everything in it happened in one game, which `groupBy: 'game'`
    // guarantees and `groupBy: 'gm'` never does.
    ref: q.groupBy === 'game' ? linkFor(rs[0]) : undefined,
  }));

  /*
   * AN UNKNOWN IS NOT A GROUP, and this is not a nicety.
   *
   * Most of the archive's older sides have never been paired to a person — `gm` is null on 2,678
   * of 3,540 — and grouping by manager therefore produces one enormous nameless entry that sorts
   * into the middle of the table as though it were a man with a twenty-year record. It drags the
   * median, it takes a rank off somebody, and read quickly it looks like a manager nobody
   * remembers. So the unknowns come out of the RANKING and go into a sentence underneath, which
   * is the honest place for "and this much of the record has no name on it".
   */
  const unknown = scored.filter((g) => g.key === 'null' || g.key === 'undefined' || g.key === '');
  const named = scored.filter((g) => !unknown.includes(g));

  /*
   * The floor, applied BEFORE ranking and reported afterwards.
   *
   * Left to itself, "lowest win percentage" answers with a man who played one game in 2009 and
   * lost it. That is true and worthless. The count of what was removed goes into the context line
   * so the floor is visible rather than something the table quietly did to the answer.
   *
   * AN AVERAGE HAS ONE EVEN WHEN NOBODY ASKED FOR ONE. "Average QB score" grouped by player put
   * Jake Browning, one week, third — above ten years of Lamar Jackson — which is not a wrong
   * number but is a wrong answer, and the kind that gets quoted. A mean over a single row is not
   * a mean, so a grouped `avg` or `pct` takes a default floor and the context line says it did.
   * Ask for `minRows` explicitly and that wins; ask for `minRows: 1` and you get everything back.
   */
  const floor = Math.max(0, q.minRows ?? (RATE_FNS.includes(q.measure.fn) ? DEFAULT_FLOOR : 0));
  const ranked = (floor > 1 ? named.filter((g) => g.rows >= floor) : named)
    .sort((a, b) => (q.sort === 'key'
      // A trend reads in group order. Numeric keys compare as numbers so 2009 sorts before 2010.
      ? cmpKey(a.key, b.key) * (desc ? -1 : 1)
      : (desc ? b.value - a.value : a.value - b.value))
      // Ties broken by the key so the same question gives the same answer twice running.
      || a.key.localeCompare(b.key));

  return {
    rows: ranked.slice(0, limit),
    matched: kept.length,
    scanned: all.length,
    examples,
    groups: scored.length,
    context: contextFor({
      q, kept, all, ranked, desc, floor,
      thin: named.length - ranked.length,
      unnamed: unknown.reduce((n, g) => n + g.rows, 0),
      total: named.length,
    }),
  };
}

/* -- the sentences around the number ---------------------------------------------------------- */

/**
 * What a person needs to know to argue with the number, in at most three plain lines.
 *
 * Each one earns its place by heading off a question the bare figure provokes:
 *
 *   HOW MUCH OF THE RECORD DID THIS SEE — because the box-score archive is not every game ever
 *   played, and a record drawn from part of the history has to say so or it will be quoted as if
 *   it were drawn from all of it.
 *
 *   WHERE DOES THE REST OF THE FIELD SIT — because "41%" means nothing until you know the league
 *   averages 50, and a leader half a point clear of second is a different story from one twenty
 *   clear.
 *
 *   WHAT WAS LEFT OUT — the thin groups a rate floor removed, said out loud.
 *
 * All four are arithmetic over the rows that were just counted. Nothing here was written by a
 * model, which is the whole point: the prose is as checkable as the figure it sits under.
 */
function contextFor(
  { q, kept, all, ranked, desc, floor, thin, unnamed, total }:
  {
    q: Query; kept: Row[]; all: Row[]; ranked: ResultRow[]; desc: boolean; floor: number;
    thin: number; unnamed: number; total: number;
  },
): string[] {
  const out: string[] = [];
  const unit = q.from === 'sides' ? 'team weeks' : 'player lines';
  const n = (v: number) => v.toLocaleString('en-US');

  // What it reached.
  if (kept.length === 0) return ['Nothing in the archive matches that.'];
  const seasons = kept.map((r) => r.season);
  const first = Math.min(...seasons);
  const last = Math.max(...seasons);
  const span = first === last ? `${first}` : `${first}–${last}`;
  out.push(
    kept.length === all.length
      ? `Every one of the ${n(all.length)} ${unit} in the archive, ${span}.`
      : `${n(kept.length)} of ${n(all.length)} ${unit} matched, ${span}.`,
  );

  // Where the rest of the field sits. Only says anything when there is a field, and only when
  // the rows are RANKED — "the leader" is a meaningless thing to say about a trend.
  if (q.groupBy && ranked.length > 1 && q.sort !== 'key') {
    const values = ranked.map((r) => r.value).sort((a, b) => a - b);
    const mid = values.length % 2
      ? values[(values.length - 1) / 2]!
      : ((values[values.length / 2 - 1]! + values[values.length / 2]!) / 2);
    const top = ranked[0]!;
    const next = ranked[1]!;
    const gap = Math.abs(top.value - next.value);
    const suffix = q.measure.fn === 'pct' ? '%' : '';
    const of = ranked.length === total ? `all ${n(total)}` : `${n(ranked.length)} ranked`;
    out.push(
      `${top.label} is the ${desc ? 'highest' : 'lowest'} of ${of}, `
      + `${gap === 0 ? `level with ${next.label}` : `${n(round(gap))}${suffix} ${desc ? 'clear of' : 'below'} ${next.label}`}`
      + `; the middle of the field is ${n(mid)}${suffix}.`,
    );
  }

  /*
   * WHERE IT ENDED UP, for a trend. A column of ten numbers is a shape nobody reads off a page,
   * and the question behind "how has this moved over ten years" is always the same one: up or
   * down, and by how much. Both ends and the difference, computed from the same rows.
   */
  if (q.groupBy && q.sort === 'key' && ranked.length > 1) {
    const first = ranked[0]!;
    const last = ranked[ranked.length - 1]!;
    const delta = round(last.value - first.value);
    const suffix = q.measure.fn === 'pct' ? '%' : '';
    out.push(delta === 0
      ? `${first.label} and ${last.label} come out level at ${n(first.value)}${suffix}.`
      : `${n(first.value)}${suffix} in ${first.label} to ${n(last.value)}${suffix} in `
        + `${last.label} — ${delta > 0 ? 'up' : 'down'} ${n(Math.abs(delta))}${suffix}.`);
  }

  /*
   * What was left out, and why. Both of these are the table being honest about its own edges —
   * and the first one has to name the floor as the app's own where the question did not set one,
   * because a number the reader did not choose and cannot see is the definition of a silent cap.
   */
  if (thin > 0) {
    const mine = q.minRows === undefined;
    out.push(
      `${n(thin)} ${thin === 1 ? 'group was' : 'groups were'} left out for having fewer than `
      + `${n(floor)} rows behind them`
      + `${mine ? ' — an average over fewer is not an average. Say "at least N" to change it.' : '.'}`,
    );
  }
  if (unnamed > 0 && q.groupBy) {
    out.push(
      `${n(unnamed)} ${unit} have no ${q.groupBy} recorded and are not ranked`
      + `${q.groupBy === 'gm' ? ' — the older seasons have never been paired to people' : ''}.`,
    );
  }

  return out;
}

/* -- what the model is told ------------------------------------------------------------------- */

/**
 * The schema, as a sentence. Sent with every question.
 *
 * Written out here rather than generated from the types because the model needs to know what the
 * fields MEAN — that `startedHim` is false for a bench line, that `won` on a line row is about the
 * team and not the player, that a null is a real state — and a type name says none of that.
 */
export function schemaPrompt(): string {
  const yrs = seasonRange();
  return [
    `The archive covers seasons ${yrs.first}–${yrs.last}. "the past ten years" and "recently" are`,
    `a WHERE on season, not something to leave out: season >= ${yrs.last - 9}.`,
    '',
    'Two row shapes:',
    '',
    'sides — one row per team per game (so two rows per matchup).',
    '  season, week (numbers) · kind (string) · gm, team, opponentGm, opponentTeam (strings; gm is',
    '  null where the archive has never been paired to a person) · points, opponentPoints, margin',
    '  (numbers, may be null) · won, tied (booleans) · started (what the starters scored) · bench',
    '  (what the bench scored) · starters (how many starting slots) · starterZeroes (how many',
    '  starters scored exactly 0) · game (an opaque matchup id, useful only for groupBy).',
    '',
    'lines — one row per player per lineup per week.',
    '  season, week, kind, gm, team, game (as above) · player (name) · slot (the lineup slot:',
    '  QB, RB, WR, TE, K, DEF, W/R/T or BN) · position (the actual position, resolved across the',
    '  whole archive; null if never known) · points (may be null where the archive did not record',
    '  it) · startedHim (false for a bench line) · won (did that TEAM win, not the player) ·',
    '  teamPoints · opponentGm.',
    '',
    'Operators: =, !=, >, >=, <, <=, in, contains.',
    'Measures: count, sum, avg, max, min, pct. Every measure but count needs a field.',
    '  pct is the share of rows where a yes/no field is true, as a percentage. The yes/no fields',
    '  are won, tied and startedHim. Rate questions — best or worst manager, who beats whom, how',
    '  often somebody is benched — are pct questions, not count questions: counting wins ranks the',
    '  managers who have been in the league longest.',
    'groupBy is optional; without it the measure runs over all matching rows at once.',
    'order is desc by default. limit defaults to 10 and is capped at 50.',
    'minRows (optional) drops groups with fewer rows than that before ranking. SET IT ON EVERY',
    '  average and every rate — 20 per manager, 40 per player — or the answer is whoever appeared',
    '  once and had a good day. Left unset on an avg or pct, the app applies a floor of five and',
    '  says on screen that it did.',
    'sort (optional): "value" ranks by the measure and is the default; "key" reads in group order.',
    '  A QUESTION ABOUT CHANGE OVER TIME WANTS sort:"key" — group by season and sort by key, or',
    '  the years come back shuffled into order of size and the trend is invisible. Anything with',
    '  "over the past", "by year", "each season" or "trend" in it is that question.',
    '',
    'Also include "reading": a short sentence, in plain words, saying what you understood the',
    'question to be asking. It MUST NOT contain any digit; it is shown above the table so a',
    'misread question can be spotted. Something else computes every number.',
  ].join('\n');
}

/** A worked example, which is worth more than any amount of instruction. */
/** What the archive actually holds, so the model can turn "the past ten years" into a filter. */
export function seasonRange(): { first: number; last: number } {
  const rows = rowsFor('sides');
  if (!rows.length) return { first: 2003, last: 2025 };
  const seasons = rows.map((r) => r.season);
  return { first: Math.min(...seasons), last: Math.max(...seasons) };
}

/** A worked TREND, because it is the shape the model gets wrong without one. */
export const TREND: { question: string; query: Query } = {
  question: 'Average QB score by season, last ten years',
  query: {
    from: 'lines',
    where: [
      { field: 'slot', op: '=', value: 'QB' },
      { field: 'startedHim', op: '=', value: true },
      { field: 'season', op: '>=', value: 2016 },
    ],
    groupBy: 'season',
    measure: { fn: 'avg', field: 'points' },
    sort: 'key',
    order: 'asc',
    limit: 20,
    reading: 'What a started quarterback scored on average, season by season.',
  },
};

export const EXAMPLE: { question: string; query: Query } = {
  question: 'Most 0.0 scores on a winning starting lineup?',
  query: {
    from: 'lines',
    where: [
      { field: 'startedHim', op: '=', value: true },
      { field: 'won', op: '=', value: true },
      { field: 'points', op: '=', value: 0 },
    ],
    groupBy: 'game',
    measure: { fn: 'count' },
    order: 'desc',
    limit: 10,
  },
};

/**
 * Parse whatever came back over the wire.
 *
 * Everything is checked rather than cast. The response is from a language model over a network; it
 * is the least trustworthy input this app has, and the difference between a bad query and a
 * crashed screen is entirely this function.
 */
export function parseQuery(raw: unknown): Query {
  if (!raw || typeof raw !== 'object') throw new QueryError('The answer was not a query at all.');
  const outer = raw as Record<string, unknown>;

  /*
   * A MODEL THAT WRAPS ITS ANSWER IS NOT WRONG, JUST TIDY.
   *
   * Asked for a query object, models quite reasonably hand back `{ "query": { ... } }` — and the
   * first version of this reported that as "'undefined' is not something to ask about", which is
   * an error message about a key nobody typed. Unwrap the obvious envelopes and get on with it.
   */
  const inner = ['query', 'result', 'response'].map((k) => outer[k])
    .find((v): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)
      && typeof (v as Record<string, unknown>)['from'] === 'string');
  const o = inner ?? outer;

  const from = o['from'];
  if (from !== 'sides' && from !== 'lines') {
    throw new QueryError(
      from === undefined
        ? 'The model answered with something that was not a query.'
        : `'${String(from)}' is not something to ask about.`,
    );
  }

  const measureRaw = o['measure'];
  if (!measureRaw || typeof measureRaw !== 'object') throw new QueryError('No measure was given.');
  const m = measureRaw as Record<string, unknown>;
  const fn = m['fn'];
  if (fn !== 'count' && fn !== 'sum' && fn !== 'avg' && fn !== 'max' && fn !== 'min'
    && fn !== 'pct') {
    throw new QueryError(`'${String(fn)}' is not a measure this can compute.`);
  }

  const where: Filter[] = [];
  const rawWhere = o['where'];
  if (rawWhere !== undefined) {
    if (!Array.isArray(rawWhere)) throw new QueryError('`where` has to be a list of conditions.');
    for (const w of rawWhere) {
      const f = w as Record<string, unknown>;
      const op = f['op'];
      if (typeof f['field'] !== 'string') throw new QueryError('A condition named no field.');
      if (!['=', '!=', '>', '>=', '<', '<=', 'in', 'contains'].includes(String(op))) {
        throw new QueryError(`'${String(op)}' is not a comparison this understands.`);
      }
      where.push({ field: f['field'], op: op as Op, value: f['value'] as Filter['value'] });
    }
  }

  const q: Query = {
    from,
    where,
    measure: { fn, field: typeof m['field'] === 'string' ? m['field'] : undefined },
    order: o['order'] === 'asc' ? 'asc' : 'desc',
    limit: typeof o['limit'] === 'number' ? o['limit'] : 10,
  };
  if (typeof o['groupBy'] === 'string') q.groupBy = o['groupBy'];
  if (o['sort'] === 'key' || o['sort'] === 'value') q.sort = o['sort'];
  if (typeof o['minRows'] === 'number' && o['minRows'] > 1) {
    q.minRows = Math.floor(o['minRows']);
  }
  const reading = readingOf(o['reading'] ?? outer['reading']);
  if (reading) q.reading = reading;

  validate(q);
  return q;
}

/**
 * The model's restatement of the question — kept only if it contains no figures.
 *
 * This is the single place prose from a model is allowed onto the screen, and the rule that makes
 * it safe is mechanical rather than a matter of prompting: A SENTENCE WITH A DIGIT IN IT IS
 * THROWN AWAY. A model cannot state a record it has not been given if it cannot state a number at
 * all, so the worst a bad reading can do is describe the wrong question — visibly, right above a
 * table that shows what was actually run.
 *
 * Words like "most" and "fewest" are fine. "41%" is not, and neither is "since 2010", because a
 * date is a claim about scope that the query itself should be making.
 */
export function readingOf(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const s = raw.trim().replace(/\s+/g, ' ');
  if (!s || s.length > 160) return undefined;
  if (/\d/.test(s)) return undefined;
  return s;
}

/*
 * Saying no lives in `askRefusals.ts`.
 *
 * It belongs with the screen rather than with the interpreter: a refusal is about a QUESTION and
 * about a model, and nothing in this file knows that either exists. What is left here is a query
 * language and the thing that runs it.
 */

/**
 * The query as a sentence.
 *
 * The result used to print the query object itself, on the argument that it was the working rather
 * than debug output. Tim's call, and he is right: a wall of JSON above every answer is a wall of
 * JSON, and almost nobody reading a league record wants to parse braces to find out what was
 * asked. The property that mattered — being able to see what the question was TURNED INTO, and
 * catch the model misreading you — survives in one line of English.
 */
export function describeQuery(q: Query): string {
  const unit = q.from === 'sides' ? 'team weeks' : 'player lines';

  const said = (f: Filter): string => {
    const v = Array.isArray(f.value) ? f.value.join(' or ')
      : f.value === true ? 'yes'
        : f.value === false ? 'no'
          : String(f.value);
    if (f.op === 'contains') return `${f.field} contains ${v}`;
    if (f.op === 'in') return `${f.field} is ${v}`;
    if (f.op === '=') return `${f.field} is ${v}`;
    if (f.op === '!=') return `${f.field} is not ${v}`;
    return `${f.field} ${f.op} ${v}`;
  };

  const where = (q.where ?? []).length
    ? ` where ${(q.where ?? []).map(said).join(' and ')}`
    : '';

  const measure = q.measure.fn === 'count'
    ? 'how many'
    : q.measure.fn === 'pct'
      ? `how often ${q.measure.field} is yes, as a percentage`
      : `${q.measure.fn === 'avg' ? 'average' : q.measure.fn} ${q.measure.field}`;

  const grouped = q.groupBy ? ` per ${q.groupBy}` : '';
  const sorted = !q.groupBy ? ''
    : q.sort === 'key'
      // A trend is read in order, not ranked, and saying "highest first" about it would be a lie.
      ? `, in ${q.groupBy} order`
      : `, ${q.order === 'asc' ? 'lowest' : 'highest'} first, top ${q.limit ?? 10}`;
  const floor = q.minRows && q.minRows > 1
    ? `, counting only those with ${q.minRows} or more`
    : '';

  return `${measure}${grouped}, across ${unit}${where}${floor}${sorted}.`;
}
